import { memo } from 'react';
import classNames from 'classnames';
import { isEmpty, isNil } from 'lodash';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Import Types
import type { RouteModel } from 'types';
import type { BlockedBySubscriptionChildrenProps } from 'components/common/BlockedBySubscriptionInfo';
// Import Hooks
import useGoToRoute from 'hooks/GoToRoute';
// Import Constants
import { BLOCKED_BY_SUBSCRIPTION_CLASSNAME } from 'constants/common';
// Import UI components
import { Icon, Tooltip } from '@geneui/components';
// Import Components
import LabelTag from 'components/common/LabelTag';

const DEFAULT_CLASSNAMES: ConfigurationWidgetProps['classNames'] = {
    wrapper: 'config-widgets-wrapper',
    btn: 'config-widgets-btn',
    icon: 'config-widgets-btn-icon',
    btnContent: 'config-widgets-btn-content',
    btnHeading: 'config-widgets-btn-heading',
    btnTitle: 'config-widgets-btn-title',
    btnTxt: 'config-widgets-btn-txt',
} as const;

const DEFAULT_SMALL_CLASSNAMES = {
    wrapper: 'config-widgets-wrapper-small',
    btnTitle: 'config-widgets-btn-heading-small',
} as const;

export interface ConfigurationWidgetProps extends BlockedBySubscriptionChildrenProps {
    title: string;
    description?: string;
    icon: string;
    rout: {
        path: RouteModel;
        params?: AnyObject;
    };
    classNames?: {
        wrapper?: string;
        btn?: string;
        icon?: string;
        btnContent?: string;
        btnHeading?: string;
        btnTitle?: string;
        btnTxt?: string;
    };
    small?: boolean;
    labelTag?: {
        color: string;
        name: string;
        bgColor?: string;
    };
    infoIcon?: string;
    isDisabledBySubscription?: boolean;
    onClick?: () => void;
}

const ConfigurationWidget: React.FC<ConfigurationWidgetProps> = (props) => {
    const {
        title,
        description = '',
        icon,
        rout,
        classNames: allClassNames = DEFAULT_CLASSNAMES,
        small = false,
        labelTag,
        infoIcon,
        isDisabledBySubscription,
        popoverActions,
        onClick,
    } = props;
    const { t } = useTranslation();
    const { getRoutePath } = useGoToRoute();

    const content = (
        <div className={allClassNames.btn}>
            <div className={allClassNames.icon}>
                <Icon type={icon} />
            </div>
            <div className={allClassNames.btnContent}>
                <div className={allClassNames.btnHeading}>
                    <span
                        className={classNames(allClassNames.btnTitle, { [DEFAULT_SMALL_CLASSNAMES.btnTitle]: small })}
                    >
                        {title}
                    </span>
                    {!isNil(infoIcon) && (
                        <Tooltip text={t(infoIcon)}>
                            <Icon type="bc-icon-info" />
                        </Tooltip>
                    )}
                </div>
                <span className={allClassNames.btnTxt}>{description}</span>
            </div>
            {!isNil(labelTag) && (
                <LabelTag color={labelTag?.color} labelName={labelTag?.name} bgColor={labelTag?.bgColor} />
            )}
        </div>
    );

    const wrapperClassNames = classNames(allClassNames.wrapper, { [DEFAULT_SMALL_CLASSNAMES.wrapper]: small });

    return isDisabledBySubscription ? (
        <div {...popoverActions} className={classNames(wrapperClassNames, BLOCKED_BY_SUBSCRIPTION_CLASSNAME)}>
            {content}
        </div>
    ) : (
        <>
            {isEmpty(rout) ? (
                <div className={wrapperClassNames}>{content}</div>
            ) : (
                <Link className={wrapperClassNames} to={getRoutePath(rout.path, rout.params)} onClick={onClick}>
                    {content}
                </Link>
            )}
        </>
    );
};

export default memo(ConfigurationWidget);
